import * as Sentry from '@sentry/react';
import { Location } from 'history';
import AppPaths from '../AppPaths';
import { webApiClient } from './fi-api/apiUtils';
import { logInternalError } from './errors';

const VERIZON_CACHE_KEY = 'verizon_validated';
const VERIZON_EXPIRATION_KEY = 'verizon_validated_expires';
const VERIZON_TOKEN_KEY = 'verizon_pdp_token';
const VERIZON_TOKEN_EXPIRATION_KEY = 'verizon_pdp_token_expires';
const CACHE_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

/**
 * Why a stored PDP token stopped being available to checkout. Losing it costs the Recurly
 * `vzUniqueIdentifier` and Verizon reconciliation for that subscription, and every one of these
 * paths used to be silent on both ends: the backend only warns about a token it was *given* and
 * could not resolve, never about one it was never sent. Two subscriptions (2026-08-19, 2026-08-21)
 * reached Recurly without an identifier and no telemetry anywhere named the cause.
 */
type VerizonPdpTokenLossReason =
  | 'gate-expired'
  | 'validation-invalid'
  | 'missing-expiration'
  | 'token-expired'
  | 'absent-at-checkout';

function reportVerizonPdpTokenLoss(reason: VerizonPdpTokenLossReason): void {
  Sentry.captureMessage(`Verizon PDP token lost: ${reason}`, {
    level: 'warning',
    fingerprint: ['verizon-pdp-token-loss', reason],
    tags: { operation: 'verizonPdpToken', reason },
  });
}

/**
 * Reports that a Verizon checkout is about to purchase with no PDP token, so the resulting
 * subscription will land in Recurly without `vzUniqueIdentifier`. Pairs 1:1 with the backend's
 * "Verizon subscription purchased without a vzUniqueIdentifier" warning.
 */
export function reportVerizonPdpTokenMissingAtCheckout(): void {
  reportVerizonPdpTokenLoss('absent-at-checkout');
}

/**
 * Checks if there's a valid cached Verizon validation
 */
function getCachedVerizonStatus(): boolean | null {
  try {
    const cachedStatus = localStorage.getItem(VERIZON_CACHE_KEY);
    const cachedExpiration = localStorage.getItem(VERIZON_EXPIRATION_KEY);

    if (cachedStatus === 'true' && cachedExpiration) {
      const expirationTime = parseInt(cachedExpiration, 10);
      if (Date.now() < expirationTime) {
        return true;
      } else {
        // Cache expired, clear it
        clearVerizonCache('gate-expired');
      }
    }

    return null;
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'cache-read-error'],
      tags: { operation: 'getCachedVerizonStatus' },
    });
    return null;
  }
}

/**
 * Records a successful Verizon validation for the UX gate. The PDP token is persisted separately
 * (see persistVerizonPdpToken) so that a transient failure of the validation call can't cost us
 * the token that checkout needs.
 */
function setCachedVerizonStatus(): void {
  try {
    localStorage.setItem(VERIZON_CACHE_KEY, 'true');
    const expirationTime = Date.now() + CACHE_DURATION_MS;
    localStorage.setItem(VERIZON_EXPIRATION_KEY, expirationTime.toString());
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'cache-write-error'],
      tags: { operation: 'setCachedVerizonStatus' },
    });
  }
}

/**
 * Persists the PDP token from the Verizon landing URL so checkout can send it on the purchase
 * mutation, where the backend resolves it to Verizon's hashed user ID and attaches it to the
 * Recurly subscription. Deliberately independent of validation success: the token is
 * reconciliation-only (never gates the sale), the backend re-validates it at checkout, and a
 * transient network failure on the non-essential validation call must not drop it.
 */
function persistVerizonPdpToken(token: string): void {
  try {
    localStorage.setItem(VERIZON_TOKEN_KEY, token);
    const expirationTime = Date.now() + CACHE_DURATION_MS;
    localStorage.setItem(VERIZON_TOKEN_EXPIRATION_KEY, expirationTime.toString());
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'token-write-error'],
      tags: { operation: 'persistVerizonPdpToken' },
    });
  }
}

function clearVerizonPdpToken(reason: VerizonPdpTokenLossReason): void {
  try {
    // Only report when there was something to lose; these paths also run on visits that never had a
    // token, and those are not incidents.
    if (localStorage.getItem(VERIZON_TOKEN_KEY)) {
      reportVerizonPdpTokenLoss(reason);
    }
    localStorage.removeItem(VERIZON_TOKEN_KEY);
    localStorage.removeItem(VERIZON_TOKEN_EXPIRATION_KEY);
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'token-clear-error'],
      tags: { operation: 'clearVerizonPdpToken' },
    });
  }
}

/**
 * Clears the Verizon validation cache
 */
function clearVerizonCache(reason: VerizonPdpTokenLossReason): void {
  try {
    localStorage.removeItem(VERIZON_CACHE_KEY);
    localStorage.removeItem(VERIZON_EXPIRATION_KEY);
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'cache-clear-error'],
      tags: { operation: 'clearVerizonCache' },
    });
  }
  clearVerizonPdpToken(reason);
}

/**
 * Returns the persisted Verizon PDP token for the purchase mutation, or null when none is stored or
 * it has aged out. Intentionally independent of the validation gate: the token is persisted on the
 * landing visit regardless of whether the validation call succeeded, so a transient failure of that
 * call no longer costs the reconciliation identifier. Its own expiration bounds how long a token can
 * linger so it isn't attached to an unrelated later purchase.
 */
export function getVerizonPdpToken(): string | null {
  try {
    const token = localStorage.getItem(VERIZON_TOKEN_KEY);
    if (!token) {
      return null;
    }
    const expiration = localStorage.getItem(VERIZON_TOKEN_EXPIRATION_KEY);
    // A token with no expiration stamp predates this change (the old flow stored the token without
    // one, gated behind the separate `verizon_validated` cache). Treat it as untrusted and clear it
    // rather than serving it forever — otherwise a legacy token could attach to an unrelated later
    // purchase.
    if (!expiration || Date.now() >= parseInt(expiration, 10)) {
      clearVerizonPdpToken(expiration ? 'token-expired' : 'missing-expiration');
      return null;
    }
    return token;
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'token-read-error'],
      tags: { operation: 'getVerizonPdpToken' },
    });
    return null;
  }
}

/**
 * Validates a Verizon token via API
 */
async function validateVerizonToken(token: string): Promise<boolean> {
  try {
    const response = await webApiClient.post('/verizon/v1/pdp/token/validate', { token });
    return response.data.valid || false;
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'api-error'],
      tags: { operation: 'validateVerizonToken' },
    });
    throw error;
  }
}

/**
 * Determines if the current user is from Verizon
 * Checks cache first, then validates token if provided, with fallback logic
 */
async function checkVerizonStatus(token: string | null, fallbackCondition: boolean): Promise<boolean> {
  // If token is provided, always validate it (bypassing cache)
  if (token) {
    // Persist the token up front, before the validation call. It's reconciliation-only and the
    // backend re-validates it at checkout, so a transient network failure of the validation call
    // below must not prevent checkout from sending it.
    persistVerizonPdpToken(token);

    try {
      const isValid = await validateVerizonToken(token);

      if (isValid) {
        // Update cache with new validation result
        setCachedVerizonStatus();
      } else {
        // Backend says the token is genuinely invalid — clear the gate and drop the token.
        clearVerizonCache('validation-invalid');
      }

      return isValid;
    } catch (error) {
      // Validation call failed (e.g. network error). Keep the persisted token so checkout still
      // sends it, and fall back for the gate decision only.
      return fallbackCondition;
    }
  }

  // No token provided, check cache first
  const cachedStatus = getCachedVerizonStatus();
  if (cachedStatus === true) {
    return true;
  }
  return fallbackCondition;
}

export async function validateVerizonStatus(location: Location, isInternalTester?: boolean) {
  if (location.pathname.includes(AppPaths.Verizon.Root)) {
    if (isInternalTester) {
      // Allow all internal testers
      return true;
    } else {
      // Check Verizon status
      try {
        // Extract token from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');

        // Fallback condition for when no token is provided or API fails
        const fallbackCondition = false;

        return checkVerizonStatus(token, fallbackCondition);
      } catch (error) {
        logInternalError(error instanceof Error ? error : new Error(String(error)), {
          fingerprint: ['verizon-validation', 'context-error'],
          tags: {
            operation: 'validateVerizonStatus',
            pathname: location.pathname,
            hasToken: !!new URLSearchParams(location.search).get('token'),
          },
        });

        return false;
      }
    }
  }
}
